import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Mail, PenLine, X } from "lucide-react";
import { NOTIFY_QK } from "../../api/notificationCentreApi";
import { draftMailto, viperLogisticsApi, type ViperActionNotice } from "../../api/viperLogisticsApi";
import { useApiQuery } from "../../hooks/useApiQuery";

// Release 1.10.0 W6 (O-R136, O-R139): a VIPER action notice (kind viper.action-proposed). The proposed action is a link —
// Open, Sign (the signing page) or Draft email (opens the person's own email client with a prefilled draft; nothing is
// sent). Accept or Dismiss records the person's decision (audited); a dismissal needs a reason. VIPER never acts.

export const VIPER_ACTION_KIND = "viper.action-proposed";
export const VIPER_ACTIONS_QK = [...NOTIFY_QK.root, "viper-actions"] as const;

const DISMISS_REASONS = ["Already handled", "Not my area", "The data is wrong"] as const;

function errorDetail(error: unknown): string {
  return (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Could not record that. Try again.";
}

export function ViperActionControls({ noticeId, onChanged, onNavigate }: { noticeId: number; onChanged: () => void; onNavigate?: () => void }) {
  const actions = useApiQuery([...VIPER_ACTIONS_QK], viperLogisticsApi.actions, { staleTime: 30_000 });
  const reasonId = useId();
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [decided, setDecided] = useState<ViperActionNotice | null>(null);

  const proposal = decided ?? actions.data?.items.find((i) => i.noticeId === noticeId) ?? null;
  if (!proposal) return null;

  const decide = async (kind: "accept" | "dismiss") => {
    setBusy(true);
    setStatus(null);
    try {
      const result = kind === "accept" ? await viperLogisticsApi.accept(noticeId) : await viperLogisticsApi.dismiss(noticeId, reason.trim());
      setDecided(result);
      setDismissing(false);
      setStatus(kind === "accept" ? "Accepted. Do the action on its own page — VIPER does nothing itself." : "Dismissed. Your reason was recorded.");
      onChanged();
    } catch (e) {
      setStatus(errorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  const button = proposal.button;
  const mailto = draftMailto(button);
  const primaryClass = "btn-primary inline-flex items-center gap-1 !px-2 !py-0.5 text-xs";
  const secondaryClass = "btn-secondary inline-flex items-center gap-1 !px-2 !py-0.5 text-xs";
  const reasonOk = reason.trim().length >= 3;

  return (
    <div className="mt-1.5 flex flex-col gap-1.5" data-testid={`viper-action-${noticeId}`}>
      <p className="text-[11px] text-slate-600">
        VIPER proposes: <span className="font-medium text-slate-800">{proposal.proposedAction}</span>
        {proposal.writtenByViper ? " (wording by VIPER, predicted)" : ""}
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        {mailto ? (
          <a href={mailto} className={primaryClass} onClick={onNavigate} data-testid="viper-action-draft">
            <Mail aria-hidden="true" className="h-3 w-3" />
            {button.label}
          </a>
        ) : (
          <Link to={button.href} className={button.kind === "sign" ? primaryClass : secondaryClass} onClick={onNavigate} data-testid={`viper-action-${button.kind}`}>
            {button.kind === "sign" ? <PenLine aria-hidden="true" className="h-3 w-3" /> : <ArrowRight aria-hidden="true" className="h-3 w-3" />}
            {button.label}
          </Link>
        )}
        {proposal.decision === "proposed" ? (
          <>
            <button type="button" className={secondaryClass} disabled={busy} onClick={() => void decide("accept")} data-testid={`viper-accept-${noticeId}`}>
              <Check aria-hidden="true" className="h-3 w-3" />
              Accept
            </button>
            <button
              type="button"
              className={secondaryClass}
              disabled={busy}
              aria-expanded={dismissing}
              onClick={() => setDismissing((v) => !v)}
              data-testid={`viper-dismiss-${noticeId}`}
            >
              <X aria-hidden="true" className="h-3 w-3" />
              Dismiss
            </button>
          </>
        ) : (
          <span className="text-[11px] font-medium text-slate-700" data-testid={`viper-decision-${noticeId}`}>
            {proposal.decision === "accepted" ? "Accepted" : `Dismissed${proposal.dismissReason ? `: ${proposal.dismissReason}` : ""}`}
          </span>
        )}
      </div>
      {dismissing && proposal.decision === "proposed" && (
        <div className="flex flex-col gap-1 rounded-md border border-slate-200 bg-white p-2">
          <div className="flex flex-wrap gap-1" role="group" aria-label="Common reasons">
            {DISMISS_REASONS.map((r) => (
              <button key={r} type="button" aria-pressed={reason === r} className="rounded-full border border-slate-300 px-2 py-0.5 text-[11px] text-slate-800 hover:bg-slate-100" onClick={() => setReason(r)}>
                {r}
              </button>
            ))}
          </div>
          <label htmlFor={reasonId} className="text-[11px] font-medium text-slate-800">
            Why are you dismissing it? (required)
          </label>
          <input id={reasonId} className="input text-xs" value={reason} onChange={(e) => setReason(e.target.value)} aria-required="true" />
          <div>
            <button type="button" className={primaryClass} disabled={busy || !reasonOk} onClick={() => void decide("dismiss")} data-testid={`viper-dismiss-confirm-${noticeId}`}>
              Dismiss with this reason
            </button>
          </div>
        </div>
      )}
      {status && (
        <p role="status" className="text-[11px] text-slate-600">
          {status}
        </p>
      )}
    </div>
  );
}
